import React from 'react'
import { useMatch, useSearchParams } from 'react-router'
import { BearerTokenPrivyWrapper } from '@components/BearerTokenPanel/BearerTokenPanel'
import { BrowseChannelsPanel } from '@components/BrowseChannelsPanel/BrowseChannelsPanel'
import { SpaceChannelWrapper } from '@components/Channel/ChannelWrapper'
import { ChannelMembersPanel } from '@components/ChannelMembersPanel/ChannelMembersPanel'
import { ChannelPermissionOverridesPanel } from '@components/ChannelSettings/ChannelPermissionOverridesPanel'
import { ChannelPermissionsNameDescriptionPanel } from '@components/ChannelSettings/ChannelPermissionsNameDescriptionForm'
import { ChannelRiverMetadataSettingsPanel } from '@components/ChannelSettings/ChannelRiverMetadataSettingsForm'
import { ClaimRewardsPanel } from '@components/ClaimRewardsPanel/ClaimRewardsPanel'
import { CreateChannelPanel } from '@components/CreateChannelPanel/CreateChannelPanel'
import { DelegateToNodeOperatorPanel } from '@components/DelegateToNodeOperatorPanel/DelegateToNodeOperatorPanel'
import { LiveParticipantsPanel } from '@components/Live/LiveParticipantsPanel'
import { MiniappPanel } from '@components/Miniapp/MiniappPanel'
import { MutualTownsPanel } from '@components/MutualTownsPanel/MutualTownsPanel'
import { NodeStatusPanel } from '@components/NodeConnectionStatusPanel/ConnectionStatusPanel'
import { Panel } from '@components/Panel/Panel'
import { PinsPanel } from '@components/PinsPanel/PinsPanel'
import { RiverPointsPanel } from '@components/PointsPanel/PointsPanel'
import { PrivyIdentityPanel } from '@components/PrivyIdentity/PrivyIdentityPanel'
import { RedelegateStakePanel } from '@components/RedelegateStakePanel/RedelegateStakePanel'
import { EditMembershipSettingsPanel } from '@components/SpaceSettingsPanel/EditMembershipSettingsPanel'
import { RolesPanel } from '@components/SpaceSettingsPanel/RolesPanel'
import { SpaceSettingsNavigationPanel } from '@components/SpaceSettingsPanel/SpaceSettingsNavigationPanel'
import { StakeToTownPanel } from '@components/StakeToTownPanel/StakeToTownPanel'
import { TipsLeaderboardPanel } from '@components/TipsLeaderboard/TipsLeaderboardPanel'
import { TownMembersPanel } from '@components/TownMembersPanel/TownMembersPanel'
import { ReviewsPanel } from '@components/TownReviews/ReviewsPanel'
import { UserPreferences } from '@components/UserProfile/UserPreferences'
import { VerifyEventSignaturePanel } from '@components/VerifyEventSignature/VerfiyEventSignature'
import { TransferFromNonDeployedSimpleAccountForm } from '@components/Web3/DetectUndeployedSimpleAccount/TransferFromNonDeployedSimpleAccountForm'
import { SingleSubscriptionPanel } from '@components/Web3/Subscriptions/SingleSubscriptionPanel'
import { SubscriptionsPanel } from '@components/Web3/Subscriptions/SubscriptionsPanel'
import { TradingDepositPanel } from '@components/Web3/Trading/TradingDepositPanel'
import { TradingPanel } from '@components/Web3/Trading/TradingPanel'
import { TradingSendPanel } from '@components/Web3/Trading/TradingSendPanel'
import { TradingWalletPanel } from '@components/Web3/Trading/TradingWalletPanel'
import { UpgradeToUsdcPanel } from '@components/Web3/UpgradeToUsdc'
import { SingleWalletPanel } from '@components/Web3/Wallet/SingleWalletPanel'
import { TransferAssetsPanel } from '@components/Web3/Wallet/TransferAssetsPanel'
import { TransferOwnershipPanel } from '@components/Web3/Wallet/TransferOwnership/TransferOwnershipPanel'
import { TreasuryTransferPanel } from '@components/Web3/Wallet/TransferTreasury/TreasuryTransferPanel'
import { WalletLinkingPanel } from '@components/Web3/WalletLinkingPanel'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useCreateLink } from 'hooks/useCreateLink'
import { CHANNEL_INFO_PARAMS, PATHS } from 'routes'
import { BotPagePanel } from 'routes/BotPagePanel'
import { BotReviewPanel } from 'routes/BotReviewPanel'
import { BotSettingsPanel } from 'routes/BotSettingsPanel'
import { BugReportPanel } from 'routes/BugReportPanel'
import { ChannelInvitePanel } from 'routes/ChannelInvitePanel'
import { DMChannelInfoPanel } from 'routes/DMChannelInfoPanel'
import { DMChannelReplies } from 'routes/DMChannelReplies'
import { RoleRestrictedChannelJoinPanel } from 'routes/RoleRestrictedChannelJoinPanel'
import { SpaceBannedUsers } from 'routes/SpaceBannedUsers'
import { ChannelInfoPanel } from 'routes/SpaceChannelInfoPanel'
import { SpaceInfoPanel } from 'routes/SpaceInfoPanel'
import { SpacesChannelReplies } from 'routes/SpacesChannelReplies'
import { SpaceProfilePanel } from 'routes/SpacesProfilePanel'
import { DMChannelContextUserLookupProvider } from 'use-towns-client'

export const usePanels = () => {
    const [searchParams] = useSearchParams()
    const { createLink } = useCreateLink()
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()

    const panel = searchParams.get('panel')

    const repliesRoute = useMatch(
        `/${PATHS.SPACES}/:spaceId/${PATHS.CHANNELS}/:channelId/${PATHS.REPLIES}/:messageId`,
    )
    const dmRepliesRoute = useMatch(`/${PATHS.MESSAGES}/:channelId/${PATHS.REPLIES}/:messageId`)
    const spaceDmRepliesRoute = useMatch(
        `/${PATHS.SPACES}/:spaceId/${PATHS.MESSAGES}/:channelId/${PATHS.REPLIES}/:messageId`,
    )

    const channelRoute = useMatch(`/${PATHS.SPACES}/:spaceId/${PATHS.CHANNELS}/:channelId/*`)
    const inboxMessageRoute = useMatch(`/${PATHS.MESSAGES}/:channelId/*`)
    const messageRoute = useMatch(`/${PATHS.SPACES}/:spaceId/${PATHS.MESSAGES}/:channelId/*`)

    const withWrapper = (children: React.JSX.Element) => {
        const channelId =
            channelRoute?.params.channelId ||
            inboxMessageRoute?.params.channelId ||
            messageRoute?.params.channelId ||
            searchParams.get('channelId')

        return channelId ? (
            <SpaceChannelWrapper channelId={channelId}>{children}</SpaceChannelWrapper>
        ) : (
            children
        )
    }

    switch (panel) {
        default: {
            break
        }
        case CHANNEL_INFO_PARAMS.NON_DEPLOYED_SIMPLE_TRANSFER: {
            return <TransferFromNonDeployedSimpleAccountForm />
        }
        case CHANNEL_INFO_PARAMS.TRANSFER_ASSETS: {
            return <TransferAssetsPanel />
        }
        case CHANNEL_INFO_PARAMS.TRANSFER_TREASURY: {
            return <TreasuryTransferPanel />
        }
        case CHANNEL_INFO_PARAMS.UPGRADE_TO_USDC: {
            return <UpgradeToUsdcPanel />
        }
        case CHANNEL_INFO_PARAMS.TRANSFER_OWNERSHIP: {
            return <TransferOwnershipPanel />
        }
        case CHANNEL_INFO_PARAMS.SINGLE_WALLET: {
            return <SingleWalletPanel />
        }
        case CHANNEL_INFO_PARAMS.BUG_REPORT: {
            return <BugReportPanel />
        }
        case CHANNEL_INFO_PARAMS.BANNED: {
            return <SpaceBannedUsers />
        }
        case CHANNEL_INFO_PARAMS.BOT_PAGE: {
            return <BotPagePanel />
        }
        case CHANNEL_INFO_PARAMS.BOT_REVIEW: {
            const botId = searchParams.get('profileId')
            if (botId) {
                return <BotReviewPanel botId={botId} />
            } else {
                return null
            }
        }
        case CHANNEL_INFO_PARAMS.BOT_SETTINGS: {
            return <BotSettingsPanel />
        }
        case CHANNEL_INFO_PARAMS.BROWSE_CHANNELS: {
            return <BrowseChannelsPanel />
        }
        case CHANNEL_INFO_PARAMS.CHANNEL_INFO: {
            return withWrapper(<ChannelInfoPanel />)
        }
        case CHANNEL_INFO_PARAMS.GDM_CHANNEL_INFO:
        case CHANNEL_INFO_PARAMS.DM_CHANNEL_INFO: {
            return withWrapper(<DMChannelInfoPanel />)
        }

        case CHANNEL_INFO_PARAMS.PROFILE: {
            const profileId = searchParams.get('profileId')
            const isDMContext = inboxMessageRoute !== null
            const channelId = isDMContext ? inboxMessageRoute?.params.channelId : undefined

            if (!profileId) {
                console.warn('[usePanels] Profile panel opened without profileId parameter')
            }
            if (isDMContext && channelId) {
                return (
                    <DMChannelContextUserLookupProvider
                        channelId={channelId}
                        abstractAccountAddress={abstractAccountAddress}
                    >
                        <SpaceProfilePanel />
                    </DMChannelContextUserLookupProvider>
                )
            }

            return <SpaceProfilePanel />
        }
        case CHANNEL_INFO_PARAMS.DIRECTORY: {
            return withWrapper(<ChannelMembersPanel />)
        }
        case CHANNEL_INFO_PARAMS.CREATE_CHANNEL: {
            return withWrapper(<CreateChannelPanel />)
        }
        case CHANNEL_INFO_PARAMS.TOWN_INFO: {
            return <SpaceInfoPanel />
        }
        case CHANNEL_INFO_PARAMS.ROLES: {
            return <RolesPanel />
        }
        case CHANNEL_INFO_PARAMS.IDENTITY: {
            return <PrivyIdentityPanel />
        }
        case CHANNEL_INFO_PARAMS.SUBSCRIPTIONS: {
            return <SubscriptionsPanel />
        }
        case CHANNEL_INFO_PARAMS.SINGLE_SUBSCRIPTION: {
            return <SingleSubscriptionPanel />
        }
        case CHANNEL_INFO_PARAMS.WALLETS: {
            return (
                <Panel label="Wallets">
                    <WalletLinkingPanel />
                </Panel>
            )
        }
        case CHANNEL_INFO_PARAMS.SPACE_SETTINGS_NAVIGATION: {
            return (
                <Panel label="Membership Settings">
                    <SpaceSettingsNavigationPanel />
                </Panel>
            )
        }
        case CHANNEL_INFO_PARAMS.EDIT_MEMBERSHIP: {
            return (
                <Panel label="Edit Membership Settings">
                    <EditMembershipSettingsPanel />
                </Panel>
            )
        }
        case CHANNEL_INFO_PARAMS.EDIT_CHANNEL_PERMISSIONS: {
            return withWrapper(<ChannelPermissionsNameDescriptionPanel />)
        }
        case CHANNEL_INFO_PARAMS.EDIT_CHANNEL_RIVER_METADATA: {
            return withWrapper(<ChannelRiverMetadataSettingsPanel />)
        }
        case CHANNEL_INFO_PARAMS.INVITE: {
            return withWrapper(<ChannelInvitePanel />)
        }

        case CHANNEL_INFO_PARAMS.PREFERENCES: {
            return (
                <Panel label="Preferences">
                    <UserPreferences />
                </Panel>
            )
        }
        case CHANNEL_INFO_PARAMS.NODE_STATUS: {
            return <NodeStatusPanel />
        }
        case CHANNEL_INFO_PARAMS.MINIAPP: {
            return withWrapper(<MiniappPanel />)
        }
        case CHANNEL_INFO_PARAMS.LIVE_PARTICIPANTS: {
            const spaceId = searchParams.get('spaceId')
            const channelId = searchParams.get('channelId')
            const channelName = searchParams.get('channelName') ?? undefined
            if (spaceId && channelId) {
                return (
                    <LiveParticipantsPanel
                        spaceId={spaceId}
                        channelId={channelId}
                        channelName={channelName}
                    />
                )
            }
            return null
        }
        case CHANNEL_INFO_PARAMS.BEARER_TOKEN: {
            return <BearerTokenPrivyWrapper />
        }
        case CHANNEL_INFO_PARAMS.ROLE_RESTRICTED_CHANNEL_JOIN: {
            return <RoleRestrictedChannelJoinPanel />
        }
        case CHANNEL_INFO_PARAMS.MUTUAL_TOWNS: {
            const userId = searchParams.get('profileId')
            if (userId) {
                return <MutualTownsPanel userId={userId} />
            } else {
                return null
            }
        }
        case CHANNEL_INFO_PARAMS.PINS: {
            return withWrapper(<PinsPanel />)
        }

        case CHANNEL_INFO_PARAMS.EDIT_CHANNEL_PERMISSION_OVERRIDES: {
            const roleId = Number(searchParams.get('roleId'))
            if (typeof roleId === 'number') {
                return withWrapper(<ChannelPermissionOverridesPanel roleId={roleId} />)
            } else {
                return null
            }
        }

        case CHANNEL_INFO_PARAMS.TOWN_MEMBERS: {
            return withWrapper(<TownMembersPanel />)
        }
        case CHANNEL_INFO_PARAMS.VERIFY_EVENT_SIGNATURE: {
            return <VerifyEventSignaturePanel />
        }
        case CHANNEL_INFO_PARAMS.RIVER_POINTS: {
            return <RiverPointsPanel />
        }
        case CHANNEL_INFO_PARAMS.TRADING_WALLET: {
            return <TradingWalletPanel />
        }
        case CHANNEL_INFO_PARAMS.TRADING_DEPOSIT: {
            return <TradingDepositPanel />
        }
        case CHANNEL_INFO_PARAMS.TRADING_SEND: {
            return <TradingSendPanel />
        }
        case CHANNEL_INFO_PARAMS.TRADE_PANEL: {
            return <TradingPanel />
        }
        case CHANNEL_INFO_PARAMS.REVIEWS: {
            return <ReviewsPanel />
        }
        case CHANNEL_INFO_PARAMS.TIPS_LEADERBOARD: {
            return <TipsLeaderboardPanel />
        }
        case CHANNEL_INFO_PARAMS.DELEGATE_TO_NODE_OPERATOR: {
            return <DelegateToNodeOperatorPanel />
        }
        case CHANNEL_INFO_PARAMS.STAKE_TO_TOWN: {
            return <StakeToTownPanel />
        }
        case CHANNEL_INFO_PARAMS.CLAIM_REWARDS: {
            return <ClaimRewardsPanel />
        }
        case CHANNEL_INFO_PARAMS.REDELEGATE_STAKE: {
            return <RedelegateStakePanel />
        }
    }

    // DM thread replies in /messages/:channelId or /t/:spaceId/messages
    const activeDmThreadRoute = dmRepliesRoute || spaceDmRepliesRoute
    if (activeDmThreadRoute) {
        const channelId = activeDmThreadRoute.params.channelId
        const messageId = activeDmThreadRoute.params.messageId
        const spaceId = spaceDmRepliesRoute?.params.spaceId
        const parentRoute = spaceId
            ? `/${PATHS.SPACES}/${spaceId}/${PATHS.MESSAGES}/${channelId}`
            : `/${PATHS.MESSAGES}/${channelId}`

        return (
            <DMChannelReplies
                channelId={channelId}
                messageId={messageId}
                spaceId={spaceId}
                parentRoute={parentRoute}
            />
        )
    }

    if (repliesRoute) {
        return withWrapper(
            <SpacesChannelReplies
                parentRoute={createLink({ channelId: repliesRoute.params.channelId })}
            />,
        )
    }

    return null
}
